/**
 * Policy Studio (P29 + P30 strip + E01 admin surface).
 *
 * Live data from `/api/v1/policies` — the deterministic decision table
 * (M-WG §4.4 + M-SPEC-V1 §6). The proxy's `PolicyRulesetLoader`
 * hot-reloads the same table every 30s (E01); a save here is live
 * without a restart.
 *
 * Author flow:
 *   1. Click "New rule" → fill the dialog → save as `draft`.
 *   2. Verify the rule reads as expected by clicking its row to re-open.
 *   3. Flip its status to `active`. The proxy picks it up on the next
 *      reload tick.
 *   4. Watch `journalctl -u sumcp-proxy | grep policy_shadow_deny` while
 *      `POLICY_ENFORCEMENT_MODE=shadow`; once the deny set looks clean,
 *      flip the env to `enforce`.
 */
import { useState, type JSX } from 'react';
import { Plus, ScrollText } from 'lucide-react';

import { usePolicies, type PolicyRuleRow } from '@/api/index.js';
import { Button } from '@/components/ui/button.js';
import { DataTable, type DataRow } from '@/components/console/DataTable.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { PolicyRuleDialog } from '@/components/console/PolicyRuleDialog.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';

export default function PolicyStudio(): JSX.Element {
  const policies = usePolicies();
  const items = policies.data?.items ?? [];

  // Editor state — null = create, a row = edit.
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PolicyRuleRow | null>(null);

  const rows: readonly DataRow[] = items.map((p) => ({
    priority: String(p.priority),
    name: p.name,
    action: p.action,
    effect: p.effect,
    reason: p.reasonCode,
    version: p.version,
    status: p.status,
  }));

  const handleRowClick = (row: DataRow): void => {
    const match = items.find(
      (p) => p.name === row.name && String(p.priority) === String(row.priority),
    );
    if (match) {
      setEditing(match);
      setOpen(true);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Govern"
        title="Policy Studio"
        description="Deterministic decision rules. Evaluated in priority order; first match wins."
      >
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus size={14} aria-hidden /> New rule
        </Button>
      </PageHeader>

      <SectionCard
        icon={ScrollText}
        title={`Active rule set (${rows.length})`}
        text="Live data from the gateway. Click a row to edit; the proxy reloads within 30 s."
      >
        <AsyncShell
          isLoading={policies.isLoading}
          isError={policies.isError}
          isEmpty={rows.length === 0}
          error={policies.error}
          loadingTitle="Loading policy rules"
          errorTitle="Policies API unreachable"
          emptyTitle="No policy rules in the registry"
          emptyHint={
            <>
              No policy rules yet. Click <strong>New rule</strong> to author the first one — start
              with <code>status: draft</code> + <code>POLICY_ENFORCEMENT_MODE=shadow</code> on the
              proxy to watch denies without blocking; flip to <code>active</code> +{' '}
              <code>enforce</code> once the deny set looks clean.
            </>
          }
        >
          <DataTable
            columns={['priority', 'name', 'action', 'effect', 'reason', 'version', 'status']}
            rows={rows}
            onRow={handleRowClick}
          />
        </AsyncShell>
      </SectionCard>

      <PolicyRuleDialog open={open} onOpenChange={setOpen} rule={editing} />
    </>
  );
}
